//src/pages/cadastros/JobPositionsPage.tsx
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  Download,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { OperationalClientRequired } from "@/components/shared/OperationalClientRequired";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { CrudRowActions } from "@/components/shared/CrudRowActions";

import {
  useJobPositions,
  useSetJobPositionActive,
} from "@/hooks/useJobPositions";
import type { JobPositionListItem } from "@/services/jobPositions";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";
import { ModelImportDialog } from "@/components/shared/ModelImportDialog";
import { useSelectedClientCompany } from "@/hooks/useSelectedClientCompany";
import { useImportJobPositionTemplates, useJobPositionTemplates } from "@/hooks/useModelImports";

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

export default function JobPositionsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientCompanyId } = useSelectedClientCompany();

  const userPermissions = user?.permissions ?? [];
  const canReadJobPositions = userPermissions.includes(PERMISSIONS.JOB_POSITIONS_READ);
  const canWriteJobPositions = userPermissions.includes(PERMISSIONS.JOB_POSITIONS_WRITE);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<JobPositionListItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const jobPositionsQ = useJobPositions(true);
  const setActiveMut = useSetJobPositionActive();
  const jobPositionTemplatesQ = useJobPositionTemplates(true);
  const importTemplatesMut = useImportJobPositionTemplates();

  const items = jobPositionsQ.data ?? [];

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return items;
    return items.filter((item) => normalizeText(item.name).includes(q));
  }, [items, search]);

  function openToggle(item: JobPositionListItem) {
    if (!canWriteJobPositions) return;
    setSelected(item);
    setConfirmOpen(true);
  }

  async function handleConfirmToggle() {
    if (!selected || !canWriteJobPositions) return;
    const nextActive = !selected.is_active;

    try {
      await setActiveMut.mutateAsync({
        id: selected.id,
        is_active: nextActive,
      });
      toast.success(nextActive ? "Cargo ativado" : "Cargo desativado");
      setConfirmOpen(false);
      setSelected(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status");
    }
  }



  async function handleImportTemplates(ids: string[]) {
    try {
      const summary = await importTemplatesMut.mutateAsync(ids);
      const imported = Object.values(summary.imported || {}).reduce((acc, value) => acc + Number(value || 0), 0);
      const skipped = Object.values(summary.skipped || {}).reduce((acc, value) => acc + Number(value || 0), 0);
      toast.success(`Importação concluída: ${imported} importado(s), ${skipped} ignorado(s).`);
      setImportOpen(false);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao importar modelos");
    }
  }

  if (!selectedClientCompanyId) {
    return (
      <div className="pb-10">
        <OperationalClientRequired />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Cargos" description="Cadastro de cargos e funções do cliente selecionado">
        <div className="flex flex-wrap gap-2">
          <PermissionButton
            permission={PERMISSIONS.JOB_POSITIONS_WRITE}
            variant="outline"
            onClick={() => setImportOpen(true)}
          >
            <Download className="mr-2 h-4 w-4" />
            Importar modelos
          </PermissionButton>
          <PermissionButton
            permission={PERMISSIONS.JOB_POSITIONS_WRITE}
            onClick={() => navigate("/cadastros/cargos/novo")}
          >
            <Plus className="mr-2 h-4 w-4" />
            Novo Cargo
          </PermissionButton>
        </div>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome do cargo"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {jobPositionsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando cargos...
        </Card>
      ) : jobPositionsQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(jobPositionsQ.error as any)?.message || "Erro ao carregar cargos"}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nenhum cargo encontrado" />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cargo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[140px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
                      {item.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/cargos/${item.id}`)}
                      onEdit={() => navigate(`/cadastros/cargos/${item.id}/editar`)}
                      onToggleActive={() => openToggle(item)}
                      isActive={item.is_active}
                      disabled={setActiveMut.isPending}
                      viewPermission={PERMISSIONS.JOB_POSITIONS_READ}
                      editPermission={PERMISSIONS.JOB_POSITIONS_WRITE}
                      toggleActivePermission={PERMISSIONS.JOB_POSITIONS_WRITE}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}


      <ModelImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Importar cargos modelo"
        description="Selecione os cargos padrão da consultoria que deseja importar para o cliente selecionado."
        templates={jobPositionTemplatesQ.data ?? []}
        loading={jobPositionTemplatesQ.isLoading}
        importing={importTemplatesMut.isPending}
        onImport={handleImportTemplates}
      />

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Cargo" : "Ativar Cargo"}
        description={
          selected?.is_active
            ? "Deseja desativar este cargo?"
            : "Deseja ativar este cargo novamente?"
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
